import clsx from 'clsx'
import React from 'react'

import config from 'tailwind.config'

import { WaitlistDialog } from '@/components/BuyingPanel/WaitlistDialog'
import {
  CART_UPDATED,
  NOTICE_ADD_TO_CART,
  SHOW_CART_MODAL,
} from '@/constants/cart-events'
import { CLEAN_BOUTIQUE_SELLING_PLAN_ID } from '@/constants/commerce'
import useMediaQuery from '@/hooks/useMediaQuery'
import { addItems } from '@/scripts/cart'
import { chord } from '@/scripts/tracking/chord'
import { transformToChordItem } from '@/scripts/tracking/helper'
import {
  isProductCardVariantEnabled,
  transformObjectToCartItemAttributes,
  variantIsAvailableForSale,
  variantIsSubscription,
} from '@/utilities/commerce'
import { openDialog } from '@/stores/dialog/actions'
import type {
  CMSButtonType,
  CMSProductCardItem,
  CMSProductCardVariant,
} from '@/types/sanity'

type Props = {
  productCardItem: CMSProductCardItem
  variantCardItem?: CMSProductCardVariant
  buttonType?: CMSButtonType
  classes?: string[]
  buttonTextProp?: string
}

export const AddToCartButton: React.FC<Props> = ({
  productCardItem,
  variantCardItem,
  buttonType = 'primaryArrow',
  classes,
  buttonTextProp,
}) => {
  const {
    slug,
    activeVariant: productActiveVariant,
    variants,
    title,
  } = productCardItem

  const activeVariant = variantCardItem || productActiveVariant

  const enabledVariants = variants.filter(
    (variant) => variant.isActive && !variant.isHidden && !variant.isDisabled,
  )
  const isSingleVariant = enabledVariants.length === 1
  const isSubscription = variantIsSubscription(activeVariant.sku)
  const isInStock = enabledVariants.some(variantIsAvailableForSale)
  const isDesktopScreen = useMediaQuery(
    `(min-width: ${config.theme.screens.lg})`,
  )

  const buttonText = buttonTextProp
    ? buttonTextProp
    : isSingleVariant && activeVariant.waitlist
      ? isDesktopScreen
        ? 'Join the waitlist'
        : 'Waitlist'
      : isInStock
        ? isSubscription && variantCardItem
          ? 'Subscribe'
          : 'Add to cart'
        : 'Sold out'

  const addToCart = async () => {
    const lineItem = {
      merchandiseId: activeVariant.gid,
      quantity: 1,
      ...(isSubscription && {
        sellingPlanId: `gid://shopify/SellingPlan/${CLEAN_BOUTIQUE_SELLING_PLAN_ID.toString()}`,
      }),
      attributes: transformObjectToCartItemAttributes({
        ...(isSubscription ? { _number_of_invoices: '1' } : {}),
      }),
    }

    const cart = await addItems([lineItem])
    window.dispatchEvent(
      new CustomEvent(NOTICE_ADD_TO_CART, {
        detail: {
          cart,
          item: activeVariant,
          cartPopupItem: activeVariant,
          product: productCardItem,
        },
      }),
    )
    window.dispatchEvent(new CustomEvent(SHOW_CART_MODAL))
    window.dispatchEvent(new CustomEvent(CART_UPDATED, { detail: cart }))
    chord?.trackProductAdded({
      cart: cart,
      product: transformToChordItem(activeVariant, productCardItem),
    })
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (
      (isSingleVariant &&
        isProductCardVariantEnabled(activeVariant) &&
        !isSubscription) ||
      variantCardItem
    ) {
      if (!activeVariant.waitlist) {
        addToCart()
      } else {
        openDialog('waitlist', {
          variantId: activeVariant.id,
        })
      }
    } else if (activeVariant.waitlist) {
      openDialog('waitlist', { variantId: activeVariant.id })
    } else {
      window.location.href = `/products/${slug}?variant=${activeVariant.id}`
    }
  }

  const buttonClasses = [
    ...(buttonType !== 'underlined'
      ? ['button', `button--${buttonType}`]
      : ['link']),
    classes,
  ]

  return (
    <>
      <button
        className={clsx(buttonClasses)}
        type="button"
        onClick={handleClick}
        disabled={!isInStock}
      >
        {buttonText}
      </button>
      {activeVariant.waitlist && (
        <WaitlistDialog
          title={title}
          product={productCardItem}
          variant={activeVariant}
        />
      )}
    </>
  )
}
